import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { isEmpty } from "lodash";

import { SaveButton, useNotify, useRedirect, useRefresh } from "react-admin";
import { useForm, useFormState } from "react-final-form";

import { getCustomFieldsToSave } from "../../../components/common/CustomFields";
import { logError } from "../../../utils/appUtils";
import { isUserFacingError } from "../../../utils/lambdaErrors";
import {
  CarrierType,
  FilePaths,
  NoteType,
  OutboundStatus,
  UserType,
} from "../../../utils/constants";
import { saveRemoveFile, saveRemoveFiles } from "../../../utils/fileUtils";
import {
  areTimeslotsEqual,
  getYardCalendarDaysInclusive,
  validateAppointment,
} from "../../../utils/dateUtils";
import {
  deepRedirect,
  getLogState,
  historyAutoGen,
  buildTimePulledNote,
  normalizeValue,
} from "../../../utils/utils";

import { resetMobileLocation } from "../../../utils/locationUtils";
import { isCollectedCleared } from "../../../utils/shipmentPlacement";
import { ConditionalConfirm } from "../../../components/common/ConditionalConfirm";
import {
  emailPOS,
  formValidate,
  getActualWeight,
  getAllInventories,
  validateOutboundBeforeUpdate,
  validateOutboundUnicity,
  validateReleasedRailcarCommit,
  formatOutboundDuplicateSaveMessage,
} from "./OutboundUtils";
import { getCarrierEquipmentTypeNameById } from "../../../utils/loadTypeUtils";
import { isLinked, shipWithSiblingIds } from "../../../utils/shipWith";
import useShipWithMembers from "../../../utils/useShipWithMembers";
import {
  fetchActiveGateCheckIn,
  GATE_TERMINAL_STATUSES,
  GateCheckInConflictError,
  resolveStagedGateEdit,
  stagedGateEditRefusal,
} from "../gateCheckIns/gateCheckInUtils";
import { runGateSideEffects } from "../gateCheckIns/onShipmentSaved";

/** The confirm-dialog line for a linked load (LTL "ship with") whose
 * appointment is changing: the lambda books every member of the group
 * together (AD-2, AD-3), so the manager is told which other loads move. The
 * count comes from the record's own id list, always in hand; the POs are the
 * members the read has returned so far, so a read still in flight, or a
 * member that failed to load, leaves the count standing alone. Null for a
 * load with no siblings. */
export const linkedAppointmentConfirmLine = (record, members = []) => {
  const siblingIds = shipWithSiblingIds(record);
  if (!siblingIds.length) return null;
  const poNumbers = (members || [])
    .filter((m) => m && m.id !== record.id && !m.unavailable && m.poNumber)
    .map((m) => m.poNumber);
  const count = siblingIds.length;
  const head = `This appointment also moves ${count} linked ${
    count === 1 ? "load" : "loads"
  }`;
  return poNumbers.length ? `${head}: PO ${poNumbers.join(", ")}` : head;
};

/** The refusal for clearing one linked load's arrival while the truck's
 * check-in is active (AD-5): the row is the whole truck's, and ending it
 * from one load's form would leave the siblings standing at the gate on a
 * visit this save just cancelled. The Gate Queue cancels the truck. */
export const LINKED_ARRIVAL_ROLLBACK_REFUSAL =
  "Cancel the truck's check-in from the Gate Queue instead";

export const DialogSaveButton = ({
  record,
  operatingAccount,
  basePath,
  backPath,
  selectedTimeslot,
  isSaving,
  setIsSaving,
  isDeleting,
  loading,
  setIsLoaded,
  setAllInventories,
  ...props
}) => {
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const history = useHistory();
  const formState = form.getState();
  const [open, setOpen] = useState(false);
  const [confirmContent, setConfirmContent] = useState("");
  // The other loads on this truck, for the appointment confirm line. Fetches
  // nothing for a load with no siblings.
  const { members: shipWithMembers } = useShipWithMembers(record);
  const saveInFlightRef = useRef(false);
  // Set in transform (where the "POS sent to:" history note is added) to
  // { record, effectiveAccount }; onSuccess sends the email only after
  // persistence. Reset at the top of every transform so a pending email
  // from a FAILED save cannot leak into a later, non-release save.
  const posEmailPendingRef = useRef(false);
  // The active check-in stranded by an arrival rollback, found in transform;
  // onSuccess cancels it only after the shipment write commits. Cancelling
  // first meant a failed save left a CANCELLED check-in still holding the
  // shipment's gateCheckInId lock, blocking the next driver.
  const pendingGateCancelRef = useRef(false);
  // { target, gateEdit } for the dispatch tab's staged gate edit, resolved
  // (and conflict-checked) in transform; onSuccess commits it only after the
  // shipment write lands. Committing in transform meant a save that failed
  // later (file upload, lambda write) had already advanced the gate row for
  // a shipment that was never saved.
  const pendingGateEditRef = useRef(false);
  // The shipment as transform is about to write it, for the manual gate
  // check-in onSuccess may mint from it. Stashed here for the same reason the
  // POS body is: the `data` onSuccess receives is the dataProvider's sanitized
  // return, and reading an arrival stamp that had been stripped out of it
  // would silently skip the row for every truck the office waves in.
  const pendingManualCheckInRef = useRef(false);
  // The driver, plate, dock and status the dispatch tab staged for a check-in
  // that has no row yet — the office checking a truck in from the form. It
  // opens the row the mint above creates, so unlike a staged edit it is never
  // resolved against an existing one.
  const pendingManualOpeningRef = useRef(false);

  const lockSave = () => {
    if (saveInFlightRef.current) return false;
    saveInFlightRef.current = true;
    setIsSaving(true);
    return true;
  };

  const unlockSave = () => {
    saveInFlightRef.current = false;
    setIsSaving(false);
  };

  const handleDialogConfirm = () => handleSave();
  const handleDialogClose = () => setOpen(false);

  const [onConfirm, setOnConfirm] = useState(() => handleDialogConfirm);

  const handleValidateSave = async () => {
    const formState = form.getState();
    const formData = formState.values;
    const tempMsg = [];
    setOnConfirm(() => handleDialogConfirm); // Allow confirmation if no error
    debugger;

    const isValid = formValidate(form, notify);
    if (!isValid) {
      handleSave({ skipLock: true });
      return;
    }

    if (!lockSave()) return;

    try {
      // A supervisor may only call the truck at the front of the gate queue,
      // and a visit only ends as DEPARTED from the dock, once the load is
      // worked. This is the last
      // point either refusal can stop the save cleanly: from the submit onward
      // a refusal only rejects react-final-form's submit promise, which
      // nothing catches, so it reached the manager as an unhandled error where
      // a warning belongs. The staged edit is left alone — the manager fixes
      // the status in the select they staged it in, or the truck's turn comes
      // and the same save then goes through.
      const refusal = await stagedGateEditRefusal(formData.gateCheckInEdit, {
        ...record,
        ...formData,
      });
      if (refusal) {
        notify(`${refusal} — nothing was saved.`, "warning");
        unlockSave();
        return;
      }

      const isOverLimit = (operatingAccount || record.account)?.overLimit;
      const isManager = window.appUser?.type === UserType.MANAGER;
      // placementDateTime is the placed time; arrivalDateTime kept as a
      // fallback for records saved before the placement backfill ran (and it
      // only errs toward requiring approval a step earlier).
      const isPlaced = formData.placementDateTime || formData.arrivalDateTime;
      if (isOverLimit && isPlaced && !isManager) {
        tempMsg.push(
          "Account is over credit limit. Requires manager approval.",
        );
        setOnConfirm(null); // Prevent confirmation, only cancel
      } else {
        // Stale outbound+lineItem+inventory validation:
        const { staleErrors } = await validateOutboundBeforeUpdate(
          formState,
          notify,
        );

        // If any staleErrors: force a refresh via Confirm
        if (staleErrors.length > 0) {
          getAllInventories(
            formData.accountId,
            formData.containerPickup,
            setAllInventories,
            setIsLoaded,
            notify,
          );

          setConfirmContent(
            <div>
              <p>Data was modified by another user while you were editing:</p>
              <ul>
                {staleErrors.map((msg, idx) => (
                  <li key={idx}>{msg}</li>
                ))}
              </ul>
              <p>Confirm to refresh and apply your edits again.</p>
            </div>,
          );
          setOnConfirm(() => () => {
            setOpen(false);
            refresh();
          });
          setOpen(true);
          unlockSave();
          return;
        }

        const oldStartDate = record.startDateTime;
        const newStartDate = formData.startDateTime;

        const duplicateNumbers = await validateOutboundUnicity(formData);
        if (duplicateNumbers.length > 0) {
          tempMsg.push(formatOutboundDuplicateSaveMessage(duplicateNumbers));
        }

        if (oldStartDate && !newStartDate) {
          tempMsg.push(
            "Rolling back will reset all inventory records to initial state. Proceed?",
          );
        }
        const actualWeight = getActualWeight(formData);
        if (actualWeight !== -1) {
          if (actualWeight !== formData.netWeight) {
            if (
              record.shipmentLineItems.items[0].inventoryItems.items.length > 1
            ) {
              tempMsg.push(
                `Can't reconcile outbound net weight, please reconcile each picklist item manually.`,
              );
            } else {
              const weightUOM = record.weightUnits;
              tempMsg.push(
                `Outbound net weight will be updated from ${formData.netWeight} to ${actualWeight} ${weightUOM}.`,
              );
            }
          }
        }

        if (selectedTimeslot) {
          if (!areTimeslotsEqual(record.timeslot, selectedTimeslot)) {
            const isBooked = await validateAppointment(
              selectedTimeslot,
              record.timeslot?.id,
            );
            if (isBooked) {
              tempMsg.push(
                `${selectedTimeslot.time} is no longer available. Please select a new time.`,
              );
              setOnConfirm(null); // Prevent confirmation if there's an error
            } else {
              setOnConfirm(() => handleDialogConfirm); // Allow confirmation if no error
            }
          }
        }

        // A linked load's appointment is its whole truck's (AD-3): the lambda
        // moves every sibling with it, so the manager is told which ones
        // before the save. Changed means another slot picked, or the
        // appointment cleared on a load that had one.
        const appointmentChanged =
          (selectedTimeslot &&
            !areTimeslotsEqual(record.timeslot, selectedTimeslot)) ||
          (record.scheduledDateTime && !formData.scheduledDateTime);
        if (appointmentChanged && isLinked(record)) {
          const line = linkedAppointmentConfirmLine(record, shipWithMembers);
          if (line) tempMsg.push(line);
        }

        // Inventory still in a railcar that has left cannot be committed.
        // Checked last: the appointment block above re-enables Confirm on
        // its happy path, and this refusal has to be the final word.
        const releasedRefusal = await validateReleasedRailcarCommit(
          formData.shipmentLineItems?.items,
        );
        if (releasedRefusal) {
          tempMsg.push(releasedRefusal);
          setOnConfirm(null); // Prevent confirmation, only cancel
        }
      }
    } catch (error) {
      unlockSave();
      logError(
        "outboundShipment:DialogSaveButton:handleValidateSave",
        error,
        notify,
      );
      return;
    }

    if (tempMsg.length === 1) {
      setConfirmContent(tempMsg[0]);
      setOpen(true);
      unlockSave();
    } else if (tempMsg.length > 1) {
      setConfirmContent(
        <ul>
          {tempMsg.map((msg) => (
            <li>{msg}</li>
          ))}
        </ul>,
      );
      setOpen(true);
      unlockSave();
    } else handleSave({ skipLock: true });
  };

  const handleSave = ({ skipLock = false } = {}) => {
    if (!skipLock && !lockSave()) return;
    setOpen(false);
    props.handleSubmitWithRedirect(); // Ensures transform is applied
  };

  const transform = async (data) => {
    setIsSaving(true);
    // A previous FAILED save may have left a POS email pending; this save
    // may no longer be a release — start clean. Same for a pending check-in
    // cancel: it must be re-derived from this save's own rollback check.
    posEmailPendingRef.current = false;
    pendingGateCancelRef.current = false;
    pendingGateEditRef.current = false;
    pendingManualCheckInRef.current = false;
    pendingManualOpeningRef.current = false;
    data = { ...data };
    debugger;

    // Pending gate check-in edit staged by the dispatch tab's inline control;
    // persisted below by this same Save. Pulled out first — it is not a
    // shipment field.
    const gateEdit = data.gateCheckInEdit;
    delete data.gateCheckInEdit;
    const actualWeight = getActualWeight(data);
    if (actualWeight !== -1) {
      data.netWeight = actualWeight;
    }

    if (data.shipmentLineItems?.items?.length > 0) {
      data.areaId = data.shipmentLineItems.items[0]?.inventory.areaId;
      data.zoneId = data.shipmentLineItems.items[0]?.inventory.zoneId;
      data.reloadYardCommodityId =
        data.shipmentLineItems.items[0]?.inventory?.reloadYardCommodity?.id;
    }

    // default billToAccountId to form value, then accountId.
    data.billToAccountId = data.billToAccountId || record.accountId;

    // Set if TRANSFER
    data.transfer =
      typeof data.transfer === "string"
        ? data.transfer
        : isEmpty(data.transfer)
          ? null
          : data.transfer[0];
    if (data.transfer) {
      // The TRANSFER marker goes on carrierName, the field the outbound forms
      // bind and every reader now checks (the Show pages' seals guard among
      // them). The legacy `carrier` scalar it used to be written to is what
      // the admin backfill moves onto carrierName, so writing it here would
      // only make new rows for that backfill to migrate. A yard-internal move
      // has no hauler, so it names no carrier record either.
      data.carrierName = "TRANSFER";
      data.carrierId = null;
      data.carrierType = CarrierType.TRUCK;
      data.railCarNumber = "TRANSFER";
      data.poNumber = data.poNumber ? data.poNumber : "TRANSFER";
      data.consigneePoNumber = "";
      data.pickupNumber = "TRANSFER";
      data.mustTarp = false;

      delete data.shipTo;
    } else {
      data.transferToAccountId = null;
      if (data.shipTo?.state)
        data.shipTo.state = data.shipTo.state.toUpperCase();
      if (data.shipTo?.zipCode)
        data.shipTo.zipCode = data.shipTo.zipCode.toUpperCase();
    }

    // Set new Outbound Status & State. PLACED keys off placementDateTime;
    // ARRIVED keys off arrivalDateTime, the driver gate check-in stamp — a
    // truck at the gate is arrived, not placed. The demurrage fallback covers
    // records placed before the placement backfill ran.
    if (data.departureDateTime && data.departureDateTime !== "") {
      data.status = OutboundStatus.RELEASED;
      if (data.carrierType === CarrierType.RAIL) {
        // Placed through released, both days counted.
        data.daysOnDemurrageClock =
          getYardCalendarDaysInclusive(
            data.placementDateTime || data.arrivalDateTime,
            data.departureDateTime,
          ) ?? 0;
      }
    } else if (data.endDateTime && data.endDateTime !== "")
      data.status = OutboundStatus.LOADED;
    else if (data.placementDateTime && data.placementDateTime !== "") {
      data.status = OutboundStatus.PLACED;
      data.state = "";
      data.daysOnDemurrageClock = 0;
    } else if (data.arrivalDateTime && data.arrivalDateTime !== "") {
      data.status = OutboundStatus.ARRIVED;
      data.state = "";
      data.daysOnDemurrageClock = 0;
    } else {
      data.status = OutboundStatus.PENDING;
      data.state = "";
      data.daysOnDemurrageClock = 0;
    }

    let isAdvance = false;
    let isRollback = false;
    // The TrackCar to take off Track Management, or to put back. The lambda
    // pulls or restores it in the same transaction as this save, so the car
    // and the shipment cannot disagree about whether it is on a track
    // whichever write fails. Never both: a pull wins.
    let trackCarPullId = null;
    let trackCarRestoreId = null;
    let statusNote = null;
    const newStatus = data.status;
    const oldStatus = formState.initialValues.status;
    const oldStartDate = formState.initialValues.startDateTime;
    const newStartDate = formState.values.startDateTime;

    if (newStatus !== oldStatus) {
      // if ((newStatus === OutboundStatus.PENDING || newStatus === OutboundStatus.ARRIVED) && (oldStartDate !== null && oldStartDate !== "")) {
      if (oldStartDate && !newStartDate) {
        isRollback = true;

        statusNote = {
          text: `Shipment reolled back | status: ${data.status} ${getLogState(
            data.state,
          )}`,
          by: window.appUser?.fullName,
          date: new Date(),
          type: NoteType.SHIPMENT,
        };
      } else {
        isAdvance = true;
        statusNote = {
          text: `Shipment advanced | status: ${data.status} ${getLogState(
            data.state,
          )}`,
          by: window.appUser?.fullName,
          date: new Date(),
          type: NoteType.SHIPMENT,
        };
      }

      data.history = data.history
        ? [...data.history, statusNote]
        : [statusNote];
    }

    const effectiveAccount = operatingAccount || data.account || record.account;

    data.customFields = getCustomFieldsToSave(
      data.customFields,
      effectiveAccount?.outboundCustomFields,
    );

    const ignoreList = [
      "status",
      "carrierType",
      "carrierEquipmentTypeId",
      "transferToAccoundId",
      "createdAt",
      "gateCheckInEdit",
      "collectedDateTime",
    ];
    const includeArrayList = ["customFields"];
    const includeObjectList = ["shipTo"];
    data.history = historyAutoGen(
      form,
      data,
      ignoreList,
      includeArrayList,
      includeObjectList,
    );
    const timePulledNote = buildTimePulledNote(record, data);
    if (timePulledNote)
      data.history = data.history
        ? [...data.history, timePulledNote]
        : [timePulledNote];

    try {
      // Rolling back past ARRIVED (arrivalDateTime cleared) strands the
      // driver's gate check-in — the crew Gate Queue and the driver's phone
      // would keep a phantom arrival. Stash it for onSuccess to cancel AFTER
      // the shipment write commits; the shipment's lock clears on this same
      // save via gateCheckInId. The history note rides this save — should the
      // later cancel fail, onSuccess raises a warning rather than hiding it.
      if (
        record.arrivalDateTime &&
        (!data.arrivalDateTime || data.arrivalDateTime === "") &&
        data.status === OutboundStatus.PENDING
      ) {
        data.gateCheckInId = null;
        // A linked load's row may sit under a sibling's key, so every
        // member's key is read and the load's own lock is tried first.
        const linked = isLinked(record);
        const strandedCheckIn = await fetchActiveGateCheckIn(
          "out",
          record.id,
          linked ? record.shipWithIds : undefined,
          linked ? record.gateCheckInId || undefined : undefined,
        );
        // That row is the whole truck's (AD-5). Cancelling it from one load's
        // form would leave the siblings at the gate on a visit this save just
        // ended, so the save is refused before anything is written — the
        // Gate Queue cancels the truck. Reported here and rejected bare, like
        // the conflict path below, so onFailure does not report it twice.
        if (strandedCheckIn && linked) {
          notify(
            `${LINKED_ARRIVAL_ROLLBACK_REFUSAL} — nothing was saved.`,
            "warning",
          );
          unlockSave();
          return Promise.reject();
        }
        if (strandedCheckIn) {
          pendingGateCancelRef.current = strandedCheckIn;
          data.history = [
            ...(data.history || []),
            {
              text: "Gate check-in cancelled — arrival rolled back",
              by: window.appUser?.fullName,
              date: new Date(),
              type: NoteType.SHIPMENT,
            },
          ];
        }
      }

      // Resolve the gate status/dock staged on the dispatch tab against the
      // row and status the manager was looking at when they staged it
      // (gateEdit.checkInId / gateEdit.baseStatus) — a row the gate crew
      // advanced or replaced meanwhile raises the conflict here and aborts
      // the whole save rather than being silently regressed. The write itself
      // waits for onSuccess, after the shipment commits. A rollback above is
      // about to cancel the row, so a staged edit (the CANCELLED pick that
      // caused the rollback) has nothing left to persist. A terminal staged
      // status will release the shipment's gateCheckInId lock when it
      // commits; mirror it in this save's own write so the shipment row does
      // not resurrect the lock.
      // A manual edit has no row behind it yet — it opens the one the mint
      // below creates, and resolving it would raise the conflict every time.
      if (gateEdit?.manual) pendingManualOpeningRef.current = gateEdit;
      else if (!pendingGateCancelRef.current && gateEdit) {
        const target = await resolveStagedGateEdit(
          "out",
          record.id,
          gateEdit,
          isLinked(record) ? record.shipWithIds : undefined,
        );
        if (target) {
          pendingGateEditRef.current = { target, gateEdit };
          if (GATE_TERMINAL_STATUSES.includes(gateEdit.status))
            data.gateCheckInId = null;
        }
      }
    } catch (err) {
      if (err instanceof GateCheckInConflictError) {
        // Drop the stale staged edit so the manager's next save isn't
        // poisoned by it; the poll shows them the row's real status.
        form.change("gateCheckInEdit", undefined);
        notify(
          "This check-in changed at the gate — nothing was saved. Review the gate status and save again.",
          "warning",
        );
      } else {
        logError("outboundShipment:DialogSaveButton:gateCheckIn", err, notify);
      }
      unlockSave();
      // Bare reject like the transform's other abort path below — the error
      // was already reported here, onFailure must not report it again.
      return Promise.reject();
    }

    if (data.carrierEquipmentTypeId !== record.carrierEquipmentTypeId) {
      const previousCarrierEquipmentTypeName = getCarrierEquipmentTypeNameById(
        record.carrierEquipmentTypeId,
      );
      const nextCarrierEquipmentTypeName = getCarrierEquipmentTypeNameById(
        data.carrierEquipmentTypeId,
      );
      const note = {
        text: `Carrier Equipment Type Changed from ${previousCarrierEquipmentTypeName} to ${nextCarrierEquipmentTypeName}`,
        by: window.appUser?.fullName,
        date: new Date(),
        type: NoteType.SHIPMENT,
      };
      data.history = data.history ? [...data.history, note] : [note];
    }
    data.scheduledDateTime = data.scheduledDateTime
      ? data.scheduledDateTime
      : null;
    data.arrivalDateTime =
      data.arrivalDateTime && data.arrivalDateTime !== ""
        ? data.arrivalDateTime
        : null;
    data.placementDateTime =
      data.placementDateTime && data.placementDateTime !== ""
        ? data.placementDateTime
        : null;
    // A load cannot be placed without having arrived. Rail loads and yards
    // without the driver kiosk never get a gate stamp, so placement fills the
    // arrival in — it also keeps arrivalDateTime populated for the composite
    // list GSIs it sorts, and for every legacy "Time Placed" reader.
    if (data.placementDateTime && !data.arrivalDateTime)
      data.arrivalDateTime = data.placementDateTime;
    data.endDateTime =
      data.endDateTime && data.endDateTime !== "" ? data.endDateTime : null;

    data.pickupNumber = data.pickupNumber ? data.pickupNumber : ""; // empty for rail

    if (data.shipTo?.state) data.shipTo.state = data.shipTo.state.toUpperCase();
    if (data.shipTo?.zipCode)
      data.shipTo.zipCode = data.shipTo.zipCode.toUpperCase();

    if (data.notes?.length > 0) {
      data.notes.forEach((element) => {
        if (!element.date) {
          element.type = NoteType.GENERIC;
          element.date = new Date();
          element.by = window.appUser?.fullName;
        }
      });
    } else data.notes = undefined;

    data.poNumber = data.poNumber ? data.poNumber : "";
    data.consigneePoNumber = data.consigneePoNumber
      ? data.consigneePoNumber
      : "";
    data.pickupNumber = data.pickupNumber ? data.pickupNumber : "";

    data.normalizedPoNumber = normalizeValue(data.poNumber);
    data.normalizedConsigneePoNumber = normalizeValue(data.consigneePoNumber);
    data.normalizedPickupNumber = normalizeValue(data.pickupNumber);
    data.normalizedRailCarNumber = normalizeValue(data.railCarNumber);

    // Every field the manual gate check-in reads is settled by now. Merged
    // over the record so a save that left a field untouched still carries it —
    // the form only holds what its own tabs put there.
    pendingManualCheckInRef.current = { ...record, ...data };

    try {
      debugger;
      // Persist selected scheduler slot in payload before lambda update call.
      data.timeslot = selectedTimeslot;
      // Remove car from track only when actually rolling back to PENDING (status changed from non-PENDING).
      // Guard prevents routine edits (notes, custom fields, etc.) on already-PENDING outbounds from clearing the track car.
      if (
        data.status === OutboundStatus.PENDING &&
        oldStatus !== OutboundStatus.PENDING &&
        record.trackCar
      ) {
        trackCarPullId = record.trackCar.id;
      }
      // Advancing back out of PENDING has to undo that, or the deactivated
      // TrackCar stays invisible to every track view for the rest of the
      // shipment's life. No-ops when the car is already active.
      if (
        data.status !== OutboundStatus.PENDING &&
        oldStatus === OutboundStatus.PENDING &&
        record.trackCar
      ) {
        trackCarRestoreId = record.trackCar.id;
      }
      // Clearing Time Collected puts the car back: isCollected off, and the
      // TrackCar the pull deactivated restored (to its last track, or to
      // Unspotted when the pull blanked it). Clearing the stamp alone left
      // both in place — billing still read the car as collected and every
      // track view still showed it gone. A rollback to PENDING in the same
      // save still takes the car off: a pending load is not on a track.
      if (isCollectedCleared(record, data)) {
        data.collectedDateTime = null;
        data.isCollected = false;
        if (record.trackCar && !trackCarPullId)
          trackCarRestoreId = record.trackCar.id;
      }
      // Files upload BEFORE the lambda write so the lambda persists the
      // final attachment keys (the trailing AppSync leg that used to save
      // them is gone — the dataProvider branch is the single writer).
      const promises = [
        saveRemoveFiles(
          record,
          data,
          "attachments",
          `${FilePaths.OUTBOUND}/${Date.now()}`,
        ),
        saveRemoveFile(
          record,
          data,
          "bolFile",
          `${FilePaths.OUTBOUND}/${Date.now()}`,
        ),
      ];
      await Promise.all(promises);

      if (
        newStatus !== oldStatus &&
        newStatus === OutboundStatus.RELEASED &&
        effectiveAccount?.posEmail
      ) {
        // Send the POS email only after persistence succeeded (onSuccess).
        // The history entry stays here — it must be part of the record the
        // lambda writes; if the email later fails it is logged non-fatally.
        // Stash the UNSANITIZED record: onSuccess only receives the
        // dataProvider's sanitized return, which lacks the connections the
        // POS body reads (shipmentLineItems, photos, seals, account).
        posEmailPendingRef.current = { record: data, effectiveAccount };

        data.history = [
          ...data.history,
          {
            text: `POS sent to: ${effectiveAccount?.posEmail}`,
            by: window.appUser?.fullName,
            date: new Date(),
            type: NoteType.GENERIC,
          },
        ];
      }
    } catch (err) {
      unlockSave();
      logError(
        "outboundShipment:DialogSaveButton:transform",
        err,
        notify,
        redirect,
      );
      return Promise.reject();
    }

    debugger;
    // NOTE: no sanitizeOutbound here — the dataProvider's outboundShipments
    // branch sanitizes on return; updateLambdaOutbound's child-diffing needs
    // the unsanitized record (shipmentLineItems.items, etc.).

    // clear extra form fields
    delete data.customFieldSelect;
    // data.saveAddress is kept: consumed by updateLambdaOutbound in the
    // dataProvider branch (addressCreateInput), stripped there afterwards.

    // clear inventorySearchDialog filters
    delete data.lotType;
    delete data.searchArea;
    delete data.searchTerm;
    delete data.showAll;

    // AppSync auto-stamped updatedAt; the direct lambda write does not.
    data.updatedAt = new Date().toISOString();
    // Carry save context to the dataProvider's outboundShipments branch;
    // stripped there before the lambda call. Its presence is also the
    // opt-in gate — RA updates without it pass through to AppSync.
    data.__saveContext = {
      isAdvance,
      isRollback,
      trackCarPullId,
      trackCarRestoreId,
    };
    return data;
  };

  const deleteContainerPickupLocation = async () => {
    if (!record?.containerPickup) {
      return;
    }

    const location =
      record?.shipmentLineItems?.items?.[0]?.inventoryItems?.items?.[0]
        ?.inventoryItem?.location;

    if (!location) {
      logError(
        "outboundShipment:DialogSaveButton:deleteContainerPickupLocation - Location not found",
        null,
        notify,
      );
      return;
    }

    try {
      await resetMobileLocation({ location });
    } catch (error) {
      logError(
        "outboundShipment:DialogSaveButton:deleteContainerPickupLocation",
        error,
        notify,
      );
    }
  };

  const onSuccess = async ({ data }) => {
    // Every gate consequence of this save, in order, shared with the inbound
    // dialog. Awaited so its warnings land before the redirect below tears the
    // form down. The refs are read and cleared here, not passed in, so a
    // re-entrant save cannot see work this one has already taken.
    const strandedCheckIn = pendingGateCancelRef.current;
    pendingGateCancelRef.current = false;
    const stagedGateEdit = pendingGateEditRef.current;
    pendingGateEditRef.current = false;
    const savedShipment = pendingManualCheckInRef.current;
    pendingManualCheckInRef.current = false;
    const manualGateOpening = pendingManualOpeningRef.current;
    pendingManualOpeningRef.current = false;
    await runGateSideEffects({
      outbound: true,
      notify,
      strandedCheckIn,
      stagedGateEdit,
      savedShipment,
      manualGateOpening,
    });

    notify("Outbound updated successfully", "success");

    if (posEmailPendingRef.current) {
      // Use the unsanitized record stashed in transform — the `data` param
      // here is the dataProvider's sanitized return, stripped of the
      // connections the POS body renders.
      const { record: posRecord, effectiveAccount } =
        posEmailPendingRef.current;
      emailPOS(
        posRecord,
        effectiveAccount?.posEmail,
        null,
        effectiveAccount?.posPhotos,
        effectiveAccount?.posAttachments,
        notify,
        effectiveAccount,
      ).catch((error) => {
        logError(
          "outboundShipment:DialogSaveButton:onSuccess:emailPOS",
          error,
          false,
        );
      });
      posEmailPendingRef.current = false;
    }

    if (data.status === OutboundStatus.RELEASED) {
      await deleteContainerPickupLocation();
      unlockSave();
      redirect(`${basePath}/${record.id}/show`);
    } else {
      unlockSave();
      deepRedirect(history, backPath ? backPath : basePath);
    }
  };

  const onFailure = (error) => {
    unlockSave();
    if (isUserFacingError(error)) {
      // The lambda names its refusals for the person saving — the crew moved
      // the car, a linked load moved on while the form was open — so those
      // read verbatim. Still recorded, without the generic toast on top.
      notify(error.message, "warning");
      logError("outboundShipment:DialogSaveButton:onFailure", error, false);
      return;
    }
    // AppSync errors carry .errors[]; lambda errors from the dataProvider
    // branch do not — guard both shapes.
    const message = error?.errors?.[0]?.message || error?.message || "";
    if (message?.startsWith("The variables input")) {
      const formState = form.getState();
      logError(
        "outboundShipment:DialogSaveButton:data",
        formState.values,
        false,
      );
    }

    logError("outboundShipment:DialogSaveButton:onFailure", error, notify);
  };

  const { dirtyFields, pristine, submitting } = useFormState({
    subscription: { dirtyFields: true, pristine: true, submitting: true },
  });
  const isPristine =
    pristine ||
    !Object.keys(dirtyFields).some(
      (key) => key !== "photos" && key !== "photos.items",
    );

  // Pass ...props, not ...rest to SaveButton to include handleSubmitWithRedirect from props
  return (
    <>
      <SaveButton
        {...props}
        label="Save"
        submitOnEnter={false}
        undoable={false}
        disabled={isPristine || isSaving || isDeleting || loading}
        saving={isSaving || submitting}
        handleSubmitWithRedirect={handleValidateSave}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <ConditionalConfirm
        isOpen={open}
        loading={isSaving}
        title="Save Confirmation"
        content={confirmContent}
        onConfirm={onConfirm}
        onClose={handleDialogClose}
      />
    </>
  );
};
